import React, { useState, useEffect } from 'react';
import { Form, Button, Table, Spinner, Alert, Card } from 'react-bootstrap';
import { FaTrash, FaPlus } from 'react-icons/fa';
import DeleteModal from '../../components/modals/DeleteModal';
import '../../styles/ViewCompanies.css';

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export default function ManageReturns() {
    const [returnReasons, setReturnReasons] = useState([]);
    const [newReason, setNewReason] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedDeleteId, setSelectedDeleteId] = useState(null);

    useEffect(() => {
        if (error || success) {
            const timer = setTimeout(() => {
                setError('');
                setSuccess('');
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [error, success]);

    useEffect(() => {
        fetchReturnReasons();
    }, []);

    const fetchReturnReasons = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${BASE_URL}/api/admin/return-reasons/get-reasons`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });
            const data = await response.json();
            if (data.success) {
                setReturnReasons(data.reason);
            } else {
                setError(data.message || 'Failed to fetch return reasons');
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddReason = async (e) => {
        e.preventDefault();
        if (!newReason.trim()) return;

        setSubmitting(true);
        setError('');
        setSuccess('');

        try {
            const response = await fetch(`${BASE_URL}/api/admin/return-reasons/add-reasons`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                },
                body: JSON.stringify({ reason: newReason })
            });
            const data = await response.json();
            if (data.success) {
                setSuccess('Return reason added successfully');
                setNewReason('');
                fetchReturnReasons();
            } else {
                setError(data.message || 'Failed to add return reason');
            }
        } catch (err) {
            console.error(err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedDeleteId(id);
        setShowDeleteModal(true);
    };

    const onDeleteSuccess = (id) => {
        setReturnReasons(prev => prev.filter(reason => reason.id !== id));
        setSuccess('Return reason deleted successfully');
    };

    return (
        <div className="vcm-admin-view-com-container mt-4">
            <div className="d-flex justify-content-between align-items-center mb-3 vcm-admin-view-com-header">
                <h1 className="vcm-admin-title m-0">Manage Returns</h1>
            </div>

            <Card className="mb-4">
                <Card.Header className="bg-white">
                    <h5 className="mb-0">Add New Return Reason</h5>
                </Card.Header>
                <Card.Body>
                    <Form onSubmit={handleAddReason} className="d-flex align-items-center gap-2">
                        <Form.Control
                            type="text"
                            placeholder="Enter return reason"
                            value={newReason}
                            onChange={(e) => setNewReason(e.target.value)}
                            disabled={submitting}
                            required
                            style={{ height: '40px', width: '80%' }}
                        />
                        <Button type="submit" variant="primary" disabled={submitting || !newReason.trim()} className="mb-2">
                            {submitting ? <Spinner animation="border" size="sm" /> : <FaPlus className="me-2" />}
                            Add Reason
                        </Button>
                    </Form>
                    {error && <Alert variant="danger" className="mt-3 py-2">{error}</Alert>}
                    {success && <Alert variant="success" className="mt-3 py-2">{success}</Alert>}
                </Card.Body>
            </Card>

            <div className="vcm-companies">
                <h5 className="mb-3">Existing Return Reasons</h5>
                {loading ? (
                    <div className="text-center p-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th style={{ width: '80px' }}>ID</th>
                                <th>Return Reason</th>
                                <th style={{ width: '200px' }}>Created At</th>
                                <th style={{ width: '100px' }} className="text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {returnReasons && returnReasons.length > 0 ? (
                                returnReasons.map((reason) => (

                                    <tr key={reason.id}>
                                        <td>{reason.id}</td>
                                        <td>{reason.reason}</td>
                                        <td>{new Date(reason.created_at).toLocaleString()}</td>
                                        <td className="text-center">
                                            <FaTrash
                                                className="text-danger"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleDeleteClick(reason.id)}
                                                title="Delete Reason"
                                            />
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="text-center py-4 text-muted">No return reasons found. Add one above.</td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            <DeleteModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                id={selectedDeleteId}
                deleteUrl={`${BASE_URL}/api/admin/return-reasons/delete/${selectedDeleteId}`}
                entityLabel="return reason"
                onDeleteSuccess={onDeleteSuccess}
                requestOptions={{
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                    }
                }}
            />
        </div>
    );
}